import { useEffect, useMemo, useState } from "react";
import { X, Calculator, Download, Banknote, Smartphone, CreditCard } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useStats } from "@/hooks/useStats";
import { useSettings } from "@/contexts/SettingsContext";
import { useBranch } from "@/contexts/BranchContext";
import { BILLING } from "@/constants/testIds";

export function ReconcileModal({ onClose }) {
  const { settings } = useSettings();
  const { activeBranchId, activeBranch } = useBranch();
  const { stats } = useStats({ branchId: activeBranchId });
  const [actual, setActual] = useState("");
  const [notes, setNotes] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const expectedCash = stats?.payment_breakdown?.cash || 0;
  const upi = stats?.payment_breakdown?.upi || 0;
  const card = stats?.payment_breakdown?.card || 0;
  const totalCollected = expectedCash + upi + card;
  const actualVal = parseFloat(actual || "0") || 0;
  const variance = actualVal - expectedCash;

  const exportPdf = async () => {
    try {
      setDownloading(true);
      const res = await fetch(`${API_BASE}/reports/reconciliation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          branch_id: activeBranchId,
          actual_cash: actualVal,
          salon_name: settings.salon_name,
          branch_name: activeBranch?.name || settings.branch_name,
          manager_name: settings.owner_name,
          notes,
        }),
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gloss-reconciliation-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reconciliation PDF downloaded", { description: "Sign and file for record" });
    } catch (e) {
      toast.error("Couldn't generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid="reconcile-modal">
      <div className="glass-elevated rounded-[24px] p-7 w-[min(600px,calc(100vw-32px))] max-h-[92vh] overflow-auto anim-slide-up-lg">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline mb-1">Cash drawer</div>
            <h2 className="font-display text-[26px] font-bold tracking-[-0.02em]">Day-End Reconciliation</h2>
            <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">{today}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        {/* Expected cash */}
        <div className="rounded-2xl p-5 mb-3" style={{ background: "linear-gradient(180deg,rgba(232,201,138,0.18),rgba(212,168,83,0.08))", border: "1px solid rgba(212,168,83,0.35)" }}>
          <div className="overline mb-1">Expected cash in drawer</div>
          <div className="font-mono leading-none text-[var(--color-gold-deeper)]" style={{ fontSize: 32 }}>₹ {expectedCash.toLocaleString("en-IN")}</div>
          <div className="font-inter text-[11.5px] text-[var(--color-gold-deeper)]/80 mt-1">Sum of all cash-method completed payments today</div>
        </div>

        {/* Actual cash */}
        <label className="block mb-3">
          <div className="overline mb-1.5">Actual cash counted</div>
          <div className="flex items-center h-14 rounded-2xl bg-white border border-white/90 focus-within:border-[var(--color-gold-mid)] focus-within:ring-2 focus-within:ring-[var(--color-gold-mid)]/20 transition">
            <span className="pl-4 pr-2 font-mono text-[18px] text-[var(--color-text-muted)]">₹</span>
            <input
              autoFocus value={actual}
              onChange={(e) => setActual(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="0"
              className="flex-1 px-1 bg-transparent font-mono text-[24px] outline-none placeholder:text-[var(--color-text-faint)]"
              data-testid={BILLING.reconcileActualInput}
            />
          </div>
        </label>

        {/* Variance */}
        <div className="rounded-2xl p-4 mb-4" style={{ background: variance < 0 ? "rgba(185,28,28,0.08)" : "rgba(22,163,74,0.08)", border: `1px solid ${variance < 0 ? "rgba(185,28,28,0.30)" : "rgba(22,163,74,0.30)"}` }}>
          <div className="flex items-center justify-between">
            <div className="overline" style={{ color: variance < 0 ? "#991B1B" : "#15803D" }}>Variance</div>
            <div className="font-mono leading-none" style={{ fontSize: 28, color: variance < 0 ? "#991B1B" : "#15803D" }}>
              ₹ {variance >= 0 ? "+" : ""}{variance.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="font-inter text-[12px] mt-1" style={{ color: variance < 0 ? "#991B1B" : "#15803D" }}>
            {actual === "" ? "Enter the actual amount above" : variance === 0 ? "Drawer balances perfectly ✨" : variance < 0 ? "Short by " + Math.abs(variance) : "Over by " + variance}
          </div>
        </div>

        {/* UPI + Card */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <ReadTile icon={Smartphone} label="UPI collected" value={upi} color="#5E4060" />
          <ReadTile icon={CreditCard} label="Card collected" value={card} color="#8E6A1A" />
        </div>

        {/* Total */}
        <div className="rounded-2xl p-5 mb-4 text-center" style={{ background: "linear-gradient(180deg,#FAF0F0,#F5E4E4)", border: "1px solid rgba(124,92,122,0.20)" }}>
          <div className="overline mb-1">Total collected · all methods</div>
          <div className="font-mono leading-none text-[var(--color-mauve-deep)]" style={{ fontSize: 36 }}>₹ {totalCollected.toLocaleString("en-IN")}</div>
        </div>

        <label className="block mb-5">
          <div className="overline mb-1.5">Notes (optional)</div>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. ₹50 short — counted in front of stylists"
                 className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
        </label>

        <div className="flex justify-end gap-3">
          <button className="btn-3d btn-glass-3d" onClick={onClose}>Close</button>
          <button className="btn-3d btn-gold-3d btn-lg" onClick={exportPdf} disabled={downloading} data-testid={BILLING.reconcileExportBtn}>
            <Download size={16} /> {downloading ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadTile({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl p-4 bg-white/55 border border-white/80">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} style={{ color }} />
        <span className="overline" style={{ color }}>{label}</span>
      </div>
      <div className="font-mono leading-none" style={{ fontSize: 24, color }}>₹ {value.toLocaleString("en-IN")}</div>
    </div>
  );
}

export default ReconcileModal;
